import { Bus } from '@/utils/bus';
import { gql } from 'graphql-tag';
import { productTransformer, BND_TYPE, FBT_TYPE } from './helpers';
import { useCartStore } from '@/stores/cart';

export default defineNuxtPlugin(async (nuxtApp) => {
  const {
    $config,
    $shopifyProductsRequest,
    $i18n: { locale },
    $craftRequest,
    $getCurrentLocaleConfig,
  } = nuxtApp;

  const query = gql`
    query PickystorySettings($site: String!) {
      pickystory: globalSet(site: [$site], handle: "pickystory") {
        ... on pickystory_GlobalSet {
          enableCartXSellTeasers
          enableUpsellTeasers
          enableXSellTeasers
        }
      }
    }
  `;

  let settings = {};

  try {
    settings = await $craftRequest(query, { site: locale.value });
  } catch (e) {
    console.warn('[Pickystory] Failed to fetch settings:', e.message || e);
  }

  let pickyEnabled = false;

  if (settings?.pickystory && (settings.pickystory.enableCartXSellTeasers || settings.pickystory.enableXSellTeasers)) {
    pickyEnabled = true;
  }

  const localeConfig = $getCurrentLocaleConfig() || {};
  const { shopifyApiAccessToken, shopifyName, currency } = localeConfig;

  const pickyScript = {
    src: 'https://widget1-cdn.heyamplify.com/widget/dist/latest/pickystory-widget.min.js',
    defer: true,
  };

  const debugToken = $config.public.pickystoryDebugToken ?? '';

  const pickySettings = {
    innerHTML: `
    window.Shopify = {
      shop: '${shopifyName}',
      currency: '${currency}',
    }
    window.pickystory = {
      storefrontApiEnable: true,
      discountDebugToken: '${debugToken}',
    };
    `,
  };

  if (pickyEnabled) {
    useHead({
      script: [pickySettings, pickyScript],
    });
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('pickystory', async (event) => {
      try {
        if (event?.detail?.name === 'API_READY') {
          console.log('Pickystory API ready event received');
          Bus.emit('pickystory-ready');
        }

        if (event?.detail?.name === 'DEAL_DISCOUNTS') {
          console.log('Pickystory DEAL_DISCOUNTS event received:', event.detail.payload);
          const cartStore = useCartStore();
          cartStore.setAsyncSubmitting(true);
          try {
            if (event.detail.payload.discount && event.detail.payload.discount.code) {
              await cartStore.refreshCheckout();
            } else {
              // we do not have any deal active so we need to clean the state of the line items
              await cartStore.refreshCheckoutAndAttributes();
            }
          } catch (error) {
            console.error('Error refreshing checkout after Picky Event:', error);
          } finally {
            cartStore.setAsyncSubmitting(false);
          }
        }
      } catch (e) {
        console.error('Error handling pickystory event:', e);
      }
    });
  }

  const initPickystoryCart = (cartId) => {
    if (typeof window === 'undefined') {
      // we cannot use window object on server side
      console.warn('Pickystory can not be initialized, window is not defined');
      return;
    }

    if (window.pickystory && window.pickystory.client) {
      try {
        window.pickystory.client.setStorefrontOptions({
          token: shopifyApiAccessToken,
          checkoutMode: false,
          cartId,
        });
      } catch (e) {
        console.error('Error initializing Pickystory cart:', e);
      }
    } else {
      // Pickystory might not be ready yet, so we can postpone the initialization
      // until the API_READY event is fired
      Bus.on('pickystory-ready', () => {
        try {
          if (window.pickystory && window.pickystory.client) {
            window.pickystory.client.setStorefrontOptions({
              token: shopifyApiAccessToken,
              checkoutMode: false,
              cartId,
            });
          }
        } catch (e) {
          console.error('Error initializing Pickystory cart from event:', e);
        }
      });
    }
  };

  const findDeal = async (location = null, productHandle, type, maxProducts = false) => {
    if (typeof window === 'undefined' || !window.pickystory || !window.pickystory.client) {
      throw new Error('Pickystory client not available');
    }

    const { client } = window.pickystory;

    try {
      const locations = client.getLocations(type);

      // if no deal group name is provided, use the first one
      const dealGroup = location ? locations.find((loc) => loc.name === location) : locations[0];

      if (!dealGroup) {
        throw new Error('Deal group not found');
      }

      // find deals based on current product
      const deals = dealGroup.allDeals
        .filter((deal) => {
          return deal.products.some((product) => {
            return product.handle === productHandle;
          });
        })
        .filter((deal) => {
          if (!maxProducts) {
            return true;
          }

          return deal.products.length <= maxProducts;
        });

      if (deals.length > 0) {
        // for now get just the first deal
        // TODO: this here would be a good place to further specify which deal to use
        const deal = deals[0];

        // get shopify information for all products in the deal
        const shopifyProducts = await $shopifyProductsRequest(
          [
            ...deal.products.map((product) => product.handle),
            ...deal.addonProducts.map((product) => product.handle),
            deal.containerProduct ? deal.containerProduct.handle : null,
          ].filter((handle) => handle !== null)
        );

        return Promise.resolve({
          pickystory: deal, // keep this as reference to call deal functions

          // transform the other stuff we may need for rendering blackroll components into properties
          id: deal.id,
          category: deal.category,
          discountType: deal.discountType,
          discountValue: deal.discountValue,
          products: deal.products.map((p) => productTransformer(p, shopifyProducts, currency)),
          discountProducts: deal.addonProducts.map((p) => productTransformer(p, shopifyProducts, currency)),
          containerProduct: deal.containerProduct
            ? productTransformer(deal.containerProduct, shopifyProducts, currency)
            : null,
          isBogo: deal.spec.discount?.target === 'addon',
        });
      }

      return null;
    } catch (e) {
      console.warn(`Error finding deal for handle ${productHandle} in ${location}:`, e.message);
      return null;
    }
  };

  return {
    provide: {
      pickystory: {
        isPickyStoryReady: () => {
          return typeof window !== 'undefined' && window.pickystory?.client && window.pickystory.storefrontApiEnable;
        },
        initPickystoryCart,
        findDeal,
        types: {
          BND_TYPE,
          FBT_TYPE,
        },
      },
    },
  };
});
